import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { proposalAPI, quotationAPI, chatAPI, pdfAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import {
  ArrowLeft,
  Clock,
  Package,
  CircleNotch,
  WarningCircle,
  CheckCircle,
  FilePdf,
  ChatCircle,
  CurrencyDollar,
  ShieldCheck,
  Handshake,
  FileText,
  Trophy,
  Phone,
  Envelope,
  Buildings,
  XCircle,
  Warning,
  Star,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import MatchitoCelebration from '../components/MatchitoCelebration';
import ContactInfoCard from '../components/ContactInfoCard';
import RatingCard from '../components/RatingCard';

const ProposalDetailPage = () => {
  const { proposalId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [contactInfo, setContactInfo] = useState(null);
  const [acceptingLoading, setAcceptingLoading] = useState(false);
  const [payingCommission, setPayingCommission] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showPayDialog, setShowPayDialog] = useState(false);
  const [showAcceptDialog, setShowAcceptDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectAck, setRejectAck] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [showMatchito, setShowMatchito] = useState(false);
  const [matchitoMsg, setMatchitoMsg] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await proposalAPI.getById(proposalId);
      setProposal(res.data);

      if (res.data.quotation_id && res.data.is_winner) {
        try {
          const ps = await quotationAPI.getPaymentStatus(res.data.quotation_id);
          setPaymentStatus(ps.data);
          if (ps.data.chat_available) {
            try {
              const ci = await chatAPI.getContactInfo(res.data.quotation_id);
              setContactInfo(ci.data);
            } catch (e) { /* ok */ }
          }
        } catch (e) { /* ok */ }
      }
    } catch (error) {
      toast.error('Error al cargar la propuesta');
    } finally { setLoading(false); }
  }, [proposalId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAcceptSale = async () => {
    setAcceptingLoading(true);
    try {
      await proposalAPI.acceptSale(proposalId);
      setMatchitoMsg('¡Venta aceptada! Ahora paga la comisión para activar el chat.');
      setShowMatchito(true);
      setShowAcceptDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al aceptar la venta');
    } finally { setAcceptingLoading(false); }
  };

  const handleRejectSale = async () => {
    setRejecting(true);
    try {
      const res = await proposalAPI.rejectSale(proposalId, rejectReason);
      const newRating = res.data?.new_rating;
      toast.success(
        newRating !== undefined
          ? `Venta rechazada. Tu nueva calificación: ${Number(newRating).toFixed(2)} ★`
          : 'Venta rechazada'
      );
      setShowRejectDialog(false);
      // Navigate the provider away — the quotation won't reappear in their dashboard
      navigate('/proposals');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al rechazar');
    } finally { setRejecting(false); }
  };

  const handlePayCommission = async () => {
    setPayingCommission(true);
    try {
      await proposalAPI.payCommission(proposalId);
      toast.success('Comisión pagada. El chat está ahora disponible.');
      setMatchitoMsg('¡Comisión pagada! El chat ya está disponible.');
      setShowMatchito(true);
      setShowPayDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al pagar comisión');
    } finally { setPayingCommission(false); }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await pdfAPI.downloadProposalPDF(proposalId);
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a'); a.href = url;
      a.download = `propuesta_${proposalId.slice(0, 12)}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (error) { toast.error('Error al descargar PDF'); }
  };

  const handleDownloadContract = async () => {
    if (!proposal?.quotation_id) return;
    try {
      const response = await quotationAPI.downloadContractPDF(proposal.quotation_id);
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a'); a.href = url;
      a.download = `contrato_${proposal.quotation_id.slice(0, 12)}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success('Contrato descargado');
    } catch (error) { toast.error('Error al descargar el contrato'); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><CircleNotch className="w-8 h-8 text-orange-500 animate-spin" /></div>;
  if (!proposal) return (
    <div className="text-center py-20">
      <WarningCircle className="w-16 h-16 text-zinc-300 mx-auto mb-4" />
      <h2 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">Propuesta no encontrada</h2>
      <Link to="/proposals" className="text-orange-600 hover:underline mt-2 inline-block">Volver</Link>
    </div>
  );

  const ps = paymentStatus;
  const commissionAmount = proposal.total ? (proposal.total * 0.03) : 0;
  const isWinner = proposal.is_winner;

  return (
    <div className="space-y-6" data-testid="proposal-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/proposals')} className="rounded-none" data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">{proposal.quotation_name}</h1>
            {isWinner && <Badge className="bg-green-500 text-white rounded-none"><Trophy className="w-3 h-3 mr-1" />Ganadora</Badge>}
          </div>
          <div className="flex items-center gap-3 mt-1 text-sm text-zinc-600 dark:text-zinc-400">
            <Badge variant="outline" className="rounded-none">{proposal.quotation_category}</Badge>
            <span>{new Date(proposal.created_at).toLocaleDateString('es-MX')}</span>
          </div>
        </div>
        <Button variant="outline" onClick={handleDownloadPDF} className="rounded-none" data-testid="download-pdf-btn">
          <FilePdf className="w-4 h-4 mr-2" />PDF
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Payment Flow for Winner */}
          {isWinner && ps && (
            <Card className="border-zinc-200 dark:border-zinc-700 rounded-none" data-testid="payment-flow-card">
              <CardHeader className="border-b border-zinc-200 dark:border-zinc-700">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Handshake className="w-5 h-5 text-orange-500" />
                  Flujo de Compra-Venta
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 sm:p-6">
                {/* Progress Steps */}
                <div className="flex items-center justify-between mb-6">
                  {[
                    { label: 'Seleccionada', done: true },
                    { label: 'Cliente confirma', done: ps.client_confirmed },
                    { label: 'Aceptar venta', done: ps.provider_accepted },
                    { label: 'Pagar 3%', done: ps.provider_paid },
                    { label: 'Chat', done: ps.chat_available },
                  ].map((step, i, arr) => (
                    <div key={step.label} className="flex items-center flex-1">
                      <div className="flex flex-col items-center">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                          step.done ? 'bg-green-500 text-white' : 'bg-zinc-200 dark:bg-zinc-700 text-zinc-500'
                        }`}>
                          {step.done ? <CheckCircle className="w-5 h-5" /> : i + 1}
                        </div>
                        <span className="text-xs mt-1 text-zinc-500 dark:text-zinc-400 text-center">{step.label}</span>
                      </div>
                      {i < arr.length - 1 && (
                        <div className={`flex-1 h-0.5 mx-1 ${step.done ? 'bg-green-400' : 'bg-zinc-200 dark:bg-zinc-700'}`} />
                      )}
                    </div>
                  ))}
                </div>

                {/* Action cards based on step */}
                {/* Provider can review + Accept Sale as soon as they are selected (no need to wait for the client's 0% confirmation) */}
                {!ps.provider_accepted && proposal.status === 'selected' && (
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-4">
                    <h4 className="font-semibold text-amber-800 dark:text-amber-300 mb-2">¡Tu propuesta fue seleccionada!</h4>
                    <p className="text-sm text-amber-700 dark:text-amber-400 mb-2">
                      Revisa el detalle de tu propuesta y, cuando estés listo, acepta la venta. Después podrás pagar la comisión del 3% para activar el chat con el cliente.
                    </p>
                    {!ps.client_confirmed && (
                      <p className="text-xs text-amber-600 dark:text-amber-500 mb-4 flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        El cliente también debe confirmar la compra (trámite sin costo).
                      </p>
                    )}
                    {ps.client_confirmed && (
                      <p className="text-xs text-green-700 dark:text-green-400 mb-4 flex items-center gap-1">
                        <CheckCircle className="w-3.5 h-3.5" />
                        El cliente ya confirmó su compra.
                      </p>
                    )}
                    <div className="flex gap-2 flex-wrap">
                      <Button className="btn-primary rounded-none" onClick={() => setShowAcceptDialog(true)} data-testid="accept-sale-btn">
                        <ShieldCheck className="w-5 h-5 mr-2" />Aceptar Venta
                      </Button>
                      <Button variant="outline" className="rounded-none border-red-300 text-red-600" onClick={() => setShowRejectDialog(true)} data-testid="reject-sale-btn">
                        <XCircle className="w-5 h-5 mr-2" />Rechazar
                      </Button>
                    </div>
                  </div>
                )}

                {ps.provider_accepted && !ps.provider_paid && (
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-4">
                    <h4 className="font-semibold text-amber-800 dark:text-amber-300 mb-2">Pagar Comisión (3%)</h4>
                    <p className="text-sm text-amber-700 dark:text-amber-400 mb-1">
                      Has aceptado la venta. Paga la comisión de la plataforma para desbloquear el chat y los datos de contacto del cliente.
                    </p>
                    <p className="text-lg font-bold text-amber-800 dark:text-amber-300 mb-4">
                      Monto: ${commissionAmount.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN
                    </p>
                    <Button className="btn-primary rounded-none" onClick={() => setShowPayDialog(true)} data-testid="pay-commission-btn">
                      <CurrencyDollar className="w-5 h-5 mr-2" />Pagar Comisión
                    </Button>
                  </div>
                )}

                {ps.provider_paid && !ps.client_confirmed && !ps.chat_available && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="w-5 h-5 text-blue-500" />
                      <h4 className="font-semibold text-blue-800 dark:text-blue-300">Esperando al Cliente</h4>
                    </div>
                    <p className="text-sm text-blue-700 dark:text-blue-400">
                      Ya aceptaste la venta y pagaste la comisión. El chat y los datos de contacto se habilitarán cuando el cliente confirme la compra en su panel.
                    </p>
                  </div>
                )}

                {ps.chat_available && (
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <h4 className="font-semibold text-green-800 dark:text-green-300">Transacción Completada</h4>
                    </div>
                    <p className="text-sm text-green-700 dark:text-green-400 mb-3">
                      El chat y la información de contacto del cliente están disponibles.
                    </p>
                    <div className="flex gap-2 flex-wrap">
                      <Link to={`/chat/${proposal.quotation_id}`}>
                        <Button variant="outline" className="rounded-none border-green-300 text-green-700" data-testid="go-to-chat-btn">
                          <ChatCircle className="w-4 h-4 mr-2" />Chat
                        </Button>
                      </Link>
                      <Button variant="outline" className="rounded-none border-orange-300 text-orange-700" onClick={handleDownloadContract} data-testid="download-contract-btn">
                        <FileText className="w-4 h-4 mr-2" />Descargar Contrato
                      </Button>
                    </div>
                  </div>
                )}

                {proposal.status === 'rejected' && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4">
                    <div className="flex items-center gap-2"><XCircle className="w-5 h-5 text-red-600" /><h4 className="font-semibold text-red-800 dark:text-red-300">Venta Rechazada</h4></div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Contact Info (after completion) */}
          {contactInfo && ps?.chat_available && (
            <ContactInfoCard
              title="Información del Cliente"
              data={contactInfo.client}
              role="client"
            />
          )}

          {/* Rating Card — appears once the contract is active and persists after closure so both parties can still rate */}
          {ps?.client_confirmed && ps?.provider_paid && proposal?.quotation_id && (
            <RatingCard
              quotationId={proposal.quotation_id}
              role="proveedor"
              counterpartName={contactInfo?.client?.business_name || contactInfo?.client?.name}
            />
          )}

          {/* Materials Table */}
          <Card className="border-zinc-200 dark:border-zinc-700 rounded-none">
            <CardHeader className="border-b border-zinc-200 dark:border-zinc-700">
              <CardTitle className="text-base">Materiales Cotizados ({proposal.items?.length || 0})</CardTitle>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-100 dark:bg-zinc-800"><tr>
                  <th className="text-left p-3 font-medium">Material</th>
                  <th className="text-center p-3 font-medium">Cant.</th>
                  <th className="text-center p-3 font-medium">Unidad</th>
                  <th className="text-right p-3 font-medium">P. Unit.</th>
                  <th className="text-right p-3 font-medium">Subtotal</th>
                </tr></thead>
                <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
                  {proposal.items?.map((item, i) => (
                    <tr key={i} className={!item.available ? 'opacity-40' : ''}>
                      <td className="p-3 font-medium">{item.material_name}{item.brand && <span className="text-xs text-zinc-400 ml-1">({item.brand})</span>}</td>
                      <td className="p-3 text-center">{item.quantity}</td>
                      <td className="p-3 text-center">{item.unit}</td>
                      <td className="p-3 text-right">${item.price?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</td>
                      <td className="p-3 text-right font-medium">${((item.price || 0) * (item.quantity || 0)).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card className="border-zinc-200 dark:border-zinc-700 rounded-none">
            <CardHeader className="border-b border-zinc-200 dark:border-zinc-700"><CardTitle className="text-base">Resumen</CardTitle></CardHeader>
            <CardContent className="p-4 space-y-3">
              <div className="flex justify-between text-sm"><span className="text-zinc-500">Subtotal</span><span className="font-medium">${proposal.subtotal?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span></div>
              {proposal.discount_percent > 0 && (
                <div className="flex justify-between text-sm text-green-600"><span>Descuento ({proposal.discount_percent}%)</span><span>-${(proposal.subtotal * proposal.discount_percent / 100)?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span></div>
              )}
              <div className="flex justify-between text-sm"><span className="text-zinc-500">IVA (16%)</span><span className="font-medium">${proposal.iva?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span></div>
              <Separator />
              <div className="flex justify-between text-lg font-bold"><span>Total</span><span className="text-orange-600">${proposal.total?.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN</span></div>
            </CardContent>
          </Card>

          {isWinner && (
            <Card className="border-zinc-200 dark:border-zinc-700 rounded-none" data-testid="commission-summary">
              <CardHeader className="border-b border-zinc-200 dark:border-zinc-700"><CardTitle className="text-base">Comisiones</CardTitle></CardHeader>
              <CardContent className="p-4 space-y-3">
                <div className="flex justify-between text-sm"><span className="text-zinc-500">Comisión Cliente</span><span className="font-medium text-green-600">$0.00 (Gratis)</span></div>
                <div className="flex justify-between text-sm"><span className="text-zinc-500">Tu Comisión (3%)</span><span className="font-bold">${commissionAmount.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN</span></div>
                <Separator />
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">{ps?.client_confirmed ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Clock className="w-4 h-4 text-zinc-400" />}<span>Cliente confirmó</span></div>
                  <div className="flex items-center gap-2">{ps?.provider_accepted ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Clock className="w-4 h-4 text-zinc-400" />}<span>Venta aceptada</span></div>
                  <div className="flex items-center gap-2">{ps?.provider_paid ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Clock className="w-4 h-4 text-zinc-400" />}<span>Comisión pagada</span></div>
                  <div className="flex items-center gap-2">{ps?.chat_available ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Clock className="w-4 h-4 text-zinc-400" />}<span>Chat activo</span></div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="border-zinc-200 dark:border-zinc-700 rounded-none">
            <CardHeader className="border-b border-zinc-200 dark:border-zinc-700"><CardTitle className="text-base">Estado</CardTitle></CardHeader>
            <CardContent className="p-4 space-y-3">
              <Badge variant="outline" className="rounded-none">{proposal.status}</Badge>
              <div><p className="text-xs text-zinc-500">Enviada</p><p className="text-sm font-medium">{new Date(proposal.created_at).toLocaleDateString('es-MX')}</p></div>
              {proposal.delivery_time && <div><p className="text-xs text-zinc-500">Entrega</p><p className="text-sm font-medium">{proposal.delivery_time}</p></div>}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Accept Dialog */}
      <Dialog open={showAcceptDialog} onOpenChange={setShowAcceptDialog}>
        <DialogContent className="rounded-none">
          <DialogHeader>
            <DialogTitle>Aceptar Venta</DialogTitle>
            <DialogDescription>
              Al aceptar, te comprometes a cumplir con la entrega de los materiales cotizados. Después deberás pagar la comisión del 3% (${commissionAmount.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN) para activar el chat.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAcceptDialog(false)} className="rounded-none">Cancelar</Button>
            <Button onClick={handleAcceptSale} disabled={acceptingLoading} className="btn-primary rounded-none" data-testid="confirm-accept-btn">
              {acceptingLoading ? <CircleNotch className="w-5 h-5 animate-spin" /> : <><ShieldCheck className="w-5 h-5 mr-2" />Aceptar Venta</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pay Commission Dialog */}
      <Dialog open={showPayDialog} onOpenChange={setShowPayDialog}>
        <DialogContent className="rounded-none">
          <DialogHeader>
            <DialogTitle>Pagar Comisión</DialogTitle>
            <DialogDescription>
              Pagarás la comisión de la plataforma del 3% sobre el total de tu propuesta.
              <br /><br />
              <strong>Monto: ${commissionAmount.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN</strong>
              <br /><br />
              Al pagar, se activará el chat con el cliente y podrás ver su información de contacto y datos fiscales.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayDialog(false)} className="rounded-none">Cancelar</Button>
            <Button onClick={handlePayCommission} disabled={payingCommission} className="btn-primary rounded-none" data-testid="confirm-pay-btn">
              {payingCommission ? <CircleNotch className="w-5 h-5 animate-spin" /> : <><CurrencyDollar className="w-5 h-5 mr-2" />Pagar ${commissionAmount.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog — destructive action with prominent warning */}
      <Dialog open={showRejectDialog} onOpenChange={(v) => { setShowRejectDialog(v); if (!v) { setRejectAck(false); setRejectReason(''); } }}>
        <DialogContent className="rounded-none sm:max-w-md max-h-[90vh] flex flex-col p-0 gap-0" data-testid="reject-dialog">
          <DialogHeader className="p-6 pb-3 border-b shrink-0">
            <DialogTitle className="flex items-center gap-2 text-red-700 dark:text-red-400">
              <Warning weight="fill" className="w-5 h-5" />
              Rechazar Venta
            </DialogTitle>
            <DialogDescription>Lee las consecuencias antes de continuar.</DialogDescription>
          </DialogHeader>

          <div className="px-6 py-4 overflow-y-auto flex-1 min-h-0 space-y-4">
            {/* Big warning box */}
            <div className="border-2 border-red-400 dark:border-red-700 bg-red-50 dark:bg-red-900/30 p-4">
              <p className="font-bold text-red-800 dark:text-red-300 text-sm mb-2">
                Si rechazas esta venta:
              </p>
              <ul className="text-sm text-red-700 dark:text-red-400 space-y-1.5 list-disc list-inside">
                <li>Tu propuesta <strong>desaparecerá del panel del cliente</strong>.</li>
                <li>Esta solicitud de cotización <strong>desaparecerá de tu tablero</strong> y no podrás volver a cotizarla.</li>
                <li>Se aplicará una <strong>penalización de -1 ★</strong> a tu calificación.</li>
                <li>El cliente podrá seleccionar otro proveedor ganador.</li>
              </ul>
            </div>

            {/* Rating preview */}
            <div className="border border-zinc-200 dark:border-zinc-700 p-3 bg-zinc-50 dark:bg-zinc-800/50">
              <p className="text-xs text-zinc-500 mb-2">Impacto en tu calificación</p>
              <div className="flex items-center gap-3">
                <div className="text-center">
                  <p className="text-[10px] text-zinc-400 uppercase">Actual</p>
                  <div className="flex items-center gap-1">
                    <Star className="w-4 h-4 text-amber-400" weight="fill" />
                    <span className="font-bold text-lg text-zinc-900 dark:text-zinc-100" data-testid="current-rating">
                      {Number(user?.rating_avg ?? 5).toFixed(2)}
                    </span>
                  </div>
                </div>
                <span className="text-xl text-zinc-400">→</span>
                <div className="text-center">
                  <p className="text-[10px] text-zinc-400 uppercase">Después del rechazo</p>
                  <div className="flex items-center gap-1">
                    <Star className="w-4 h-4 text-red-400" weight="fill" />
                    <span className="font-bold text-lg text-red-600 dark:text-red-400" data-testid="new-rating-preview">
                      {Math.max(0, Number(user?.rating_avg ?? 5) - 1).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Reason */}
            <div>
              <label className="text-xs text-zinc-500 mb-2 block" htmlFor="reject-reason">
                Razón del rechazo <span className="text-zinc-400">(recomendada, se envía al cliente)</span>
              </label>
              <Textarea
                id="reject-reason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Ej. No tengo disponibilidad para cumplir la fecha de entrega..."
                className="rounded-none"
                rows={3}
                maxLength={300}
                data-testid="reject-reason"
              />
              <p className="text-xs text-zinc-400 text-right mt-1">{rejectReason.length}/300</p>
            </div>

            {/* Acknowledgement */}
            <label className="flex items-start gap-2 p-3 border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/50 cursor-pointer" data-testid="reject-ack-label">
              <input
                type="checkbox"
                checked={rejectAck}
                onChange={(e) => setRejectAck(e.target.checked)}
                className="mt-1 accent-red-600"
                data-testid="reject-ack-checkbox"
              />
              <span className="text-sm text-zinc-800 dark:text-zinc-200">
                Entiendo que mi propuesta desaparecerá, no podré volver a cotizar esta solicitud y se aplicará la penalización de -1 estrella.
              </span>
            </label>
          </div>

          <DialogFooter className="p-6 pt-4 border-t bg-white dark:bg-zinc-900 shrink-0 sm:flex-row flex-col gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setShowRejectDialog(false)} className="rounded-none w-full sm:w-auto" data-testid="reject-cancel-btn">
              Cancelar
            </Button>
            <Button
              onClick={handleRejectSale}
              disabled={rejecting || !rejectAck}
              variant="destructive"
              className="rounded-none disabled:opacity-50 w-full sm:w-auto"
              data-testid="confirm-reject-btn"
            >
              {rejecting ? <CircleNotch className="w-5 h-5 animate-spin" /> : 'Confirmar rechazo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <MatchitoCelebration show={showMatchito} message={matchitoMsg} onClose={() => setShowMatchito(false)} />
    </div>
  );
};

export default ProposalDetailPage;
